'use client';

import Link from 'next/link';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { processAmazonUploads } from './server-actions';

type KPIs = { spend: number; sales: number; net: number; acos: number|null; roas: number|null; currency: 'INR' };

export default function UploadAmazonPage() {
  const [filesUI, setFilesUI] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const [state, dispatch, isPending] = (React as any).useActionState(
    processAmazonUploads,
    { ok: false, error: '', payload: null as any, notes: [] as string[] }
  );

  const onPick = useCallback(() => inputRef.current?.click(), []);
  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.files ? Array.from(e.target.files) : [];
    setFilesUI((prev) => dedupe([...prev, ...next]));
    // keep files on the input so they submit with the form
  }, []);
  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const next = Array.from(e.dataTransfer.files || []);
    setFilesUI((prev) => dedupe([...prev, ...next]));
    try {
      const dt = new DataTransfer();
      next.forEach(f => dt.items.add(f));
      if (inputRef.current) inputRef.current.files = dt.files;
    } catch {}
  }, []);
  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => e.preventDefault(), []);
  const clearAll = useCallback(() => {
    setFilesUI([]);
    if (inputRef.current) inputRef.current.value = '';
  }, []);
  const totalSize = useMemo(() => filesUI.reduce((a, f) => a + f.size, 0), [filesUI]);

  const payload = state.payload as (null | {
    meta: any;
    by_channel: { SP: KPIs; SB: KPIs; SD: KPIs };
    totals: KPIs;
    short_summary: string;
  });

  return (
    <main className="min-h-screen bg-white text-neutral-900">
      <header className="border-b">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link href="/" className="text-xl font-bold"><span className="text-indigo-600">Ad</span>Wisely</Link>
          <Link href="/connections" className="text-sm text-neutral-600 hover:text-neutral-900">← Back</Link>
        </div>
      </header>

      <section className="mx-auto max-w-3xl px-4 py-10">
        <div className="mb-2 text-xs uppercase tracking-wide text-neutral-500">amazon</div>
        <h1 className="text-3xl font-semibold">Amazon Ads — Upload Campaign Reports</h1>
        <p className="mt-2 text-neutral-600">Upload SP (Campaign), SB (Campaign), and SD (Campaign). Files are parsed in-memory and not stored.</p>

        <div className="mt-6 rounded-xl border bg-neutral-50 p-4">
          <div className="text-sm font-medium mb-2">Files to include</div>
          <ul className="list-disc pl-5 text-sm text-neutral-700 space-y-1">
            <li>SP: <code>Sponsored_Products_Campaign_report*.csv</code></li>
            <li>SB: <code>Sponsored_Brands_Campaign_report*.xlsx</code></li>
            <li>SD: <code>Sponsored_Display_Campaign_report*.xlsx</code></li>
          </ul>
        </div>

        <form action={dispatch} encType="multipart/form-data" className="mt-6">
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            className="rounded-2xl border-2 border-dashed p-8 text-center hover:bg-neutral-50 transition"
            role="button"
            onClick={onPick}
            aria-label="Add files"
          >
            <input
              ref={inputRef}
              type="file"
              name="files"
              multiple
              accept=".csv,.xlsx"
              className="hidden"
              onChange={onInputChange}
            />
            <div className="mx-auto w-12 h-12 rounded-full border flex items-center justify-center mb-3">
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden className="opacity-70">
                <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="font-medium">Drag & drop files here</div>
            <div className="text-sm text-neutral-600 mt-1">or click to browse (CSV, XLSX)</div>
            {!!filesUI.length && (
              <div className="mt-3 text-xs text-neutral-500">
                {filesUI.length} file{filesUI.length > 1 ? 's' : ''} selected • {formatBytes(totalSize)}
              </div>
            )}
          </div>

          {!!filesUI.length && (
            <div className="mt-6 rounded-xl border">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="text-sm font-medium">Selected files</div>
                <button type="button" onClick={clearAll} className="text-xs text-neutral-600 hover:text-neutral-900 underline">Clear all</button>
              </div>
              <ul className="max-h-72 overflow-auto divide-y">
                {filesUI.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center justify-between px-4 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm">{f.name}</div>
                      <div className="text-xs text-neutral-500">{formatBytes(f.size)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex items-center gap-3">
            <button
              type="submit"
              className="rounded-xl bg-black text-white px-5 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-50"
              disabled={!filesUI.length || isPending}
            >
              {isPending ? 'Generating…' : 'Generate Summary'}
            </button>
            <span className="text-xs text-neutral-500">SP uses 7-day sales; SB/SD use 14-day.</span>
          </div>
        </form>

        {state.ok && payload && (
          <div className="mt-8 space-y-6">
            <h3 className="text-xl font-semibold">Topline Summary</h3>

            <div className="grid md:grid-cols-3 gap-4">
              <Card label="Total Spend" value={`₹${fmtINR(payload.totals.spend)}`} />
              <Card label="Total Sales" value={`₹${fmtINR(payload.totals.sales)}`} />
              <Card label="Net" value={`₹${fmtINR(payload.totals.net)}`} />
              <Card label="ACOS" value={payload.totals.acos == null ? '—' : `${(payload.totals.acos*100).toFixed(2)}%`} />
              <Card label="ROAS" value={payload.totals.roas == null ? '—' : `${payload.totals.roas.toFixed(2)}x`} />
            </div>

            <div className="rounded-xl border">
              <div className="px-4 py-3 border-b text-sm font-medium">By Channel</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-neutral-50">
                    <tr><Th>Channel</Th><Th>Spend (₹)</Th><Th>Sales (₹)</Th><Th>Net (₹)</Th><Th>ACOS</Th><Th>ROAS</Th></tr>
                  </thead>
                  <tbody>
                    {(['SP','SB','SD'] as const).map(ch => {
                      const k = payload.by_channel[ch];
                      return (
                        <tr key={ch} className="border-t">
                          <Td>{ch}</Td>
                          <Td>{fmtINR(k.spend)}</Td>
                          <Td>{fmtINR(k.sales)}</Td>
                          <Td>{fmtINR(k.net)}</Td>
                          <Td>{k.acos==null?'—':`${(k.acos*100).toFixed(2)}%`}</Td>
                          <Td>{k.roas==null?'—':`${k.roas.toFixed(2)}x`}</Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {!!state.notes?.length && (
              <div className="rounded-xl border p-4">
                <div className="text-sm font-medium mb-2">Notes</div>
                <ul className="list-disc pl-5 text-sm text-neutral-700 space-y-1">
                  {state.notes.map((n: string, i: number) => <li key={i}>{n}</li>)}
                </ul>
              </div>
            )}

            <div className="rounded-xl border overflow-hidden">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <div className="text-sm font-medium">Payload (amazon_topline_summary.json)</div>
                <button onClick={() => downloadJSON(payload, 'amazon_topline_summary.json')} className="text-xs px-2 py-1 rounded-lg border hover:bg-neutral-50">Download JSON</button>
              </div>
              <pre className="p-4 text-xs overflow-auto">{JSON.stringify(payload, null, 2)}</pre>
            </div>
          </div>
        )}

        {state.error && !state.ok ? <p className="mt-6 text-sm text-red-600">{state.error}</p> : null}
      </section>
    </main>
  );
}

/* helpers */
function fmtINR(n: number) { try { return Math.round(n).toLocaleString('en-IN'); } catch { return String(Math.round(n)); } }
function formatBytes(bytes: number) { if (bytes===0) return '0 B'; const k=1024, s=['B','KB','MB','GB']; const i=Math.floor(Math.log(bytes)/Math.log(k)); return `${(bytes/Math.pow(k,i)).toFixed(1)} ${s[i]}`; }
function dedupe(list: File[]) { const seen = new Set<string>(); const out: File[] = []; for (const f of list) { const k = `${f.name}::${f.size}`; if (!seen.has(k)) { seen.add(k); out.push(f); } } return out; }
function downloadJSON(obj: any, filename: string) { const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url); }
function Card({ label, value }: { label: string; value: string }) { return (<div className="rounded-xl border p-4"><div className="text-xs text-neutral-500">{label}</div><div className="text-lg font-semibold">{value}</div></div>); }
function Th({ children }: { children: React.ReactNode }) { return <th className="text-left px-4 py-2 font-medium text-neutral-700">{children}</th>; }
function Td({ children }: { children: React.ReactNode }) { return <td className="px-4 py-2">{children}</td>; }
